/**
 * The particle effects: missile and boost trails, the shockwave of a repel,
 * damage sparks, and the explosions of deaths and missile hits. Three
 * containers draw them, smoke behind the planes, explosions above them, and
 * the shadow of every puff on the shadow layer. The spawners draw their
 * randoms in legacy's order. The hitshapes theme skips the smoke clouds, and
 * for a missile hit the glow and hot smoke too, as SWAM did; the explosion
 * light is this client's addition from the missile-light work.
 */
import { MOB_TYPES } from '@airmash/protocol';
import { clock } from '../Clock';
import { DISPLAY_DEFAULTS } from '../Config';
import { PlaneType, TWO_PI } from '../Constants';
import { layers } from '../Layers';
import type { Mob } from '../Mob';
import type Player from '../Player';
import { settings } from '../Settings';
import { rand as toolsRand, randCircle as toolsRandCircle, randItem as toolsRandItem } from '../Tools';
import Vector from '../Vector';
import { ParticleContainer } from './ParticleContainer';
import {
  flashTextures,
  hotsmokeTextures,
  type ParticleTypeId,
  particleTypeIdByName,
  smokePairs,
  smokeTextures,
  sparkTextures,
} from './ParticleTypes';

/** Each container's cap, as legacy had it. */
const MAX_PARTICLES = 2000;

/** The additive tint of sparks and hot smoke. */
const SPARK_TINT = 0xff6b01;

/** The additive tint of an explosion's two flashes. */
const FLASH_TINT = 0xf3f3ac;

/** The tint a missile or boost trail starts from, fading to white. */
const SMOKE_TINT = 0xfff9a6;

/** The trail each missile leaves; a mob type absent here leaves none. */
const TRAIL_BY_MISSILE: Readonly<Partial<Record<MOB_TYPES, ParticleTypeId>>> = {
  [MOB_TYPES.PREDATOR_MISSILE]: particleTypeIdByName.MISSILE,
  [MOB_TYPES.GOLIATH_MISSILE]: particleTypeIdByName.MISSILE_FAT,
  [MOB_TYPES.COPTER_MISSILE]: particleTypeIdByName.MISSILE_SMALL,
  [MOB_TYPES.TORNADO_MISSILE]: particleTypeIdByName.MISSILE,
  [MOB_TYPES.TORNADO_SMALL_MISSILE]: particleTypeIdByName.MISSILE,
  [MOB_TYPES.PROWLER_MISSILE]: particleTypeIdByName.MISSILE,
};

let smoke: ParticleContainer;
let explosions: ParticleContainer;
let shadows: ParticleContainer;

function isHitshapes(): boolean {
  return (settings.display?.theme ?? DISPLAY_DEFAULTS.theme) === 'hitshapes';
}

/** Creates the three containers in their layers; the textures must be loaded. */
export function setup() {
  smoke = new ParticleContainer(layers.smoke, MAX_PARTICLES);
  shadows = new ParticleContainer(layers.shadows, MAX_PARTICLES, { shadow: true });
  // The additive flashes, sparks, glow and hot smoke draw as a group over the explosion and debris smoke
  explosions = new ParticleContainer(layers.explosions, MAX_PARTICLES, { blendModes: ['normal', 'add'] });
}

/** Steps every container once per 60 fps frame the elapsed time holds, so a long frame animates the same. */
export function update() {
  const iterations = clock.timeFactor > 0.51 ? Math.round(clock.timeFactor) : 1;
  const timeStep = clock.timeFactor / iterations;
  for (let i = 0; i < iterations; i++) {
    smoke.update(timeStep);
    explosions.update(timeStep);
    shadows.update(timeStep);
  }
}

/** Clears every particle, on a game reset. */
export function wipe() {
  smoke.wipe();
  explosions.wipe();
  shadows.wipe();
}

/** One spark off a damaged plane, thrown from a spot near its centre and carried with it. */
export function planeDamage(player: Player) {
  const reach = player.type === PlaneType.Goliath ? 2 : 1;
  const texture = toolsRandItem(sparkTextures);
  const angle = toolsRandCircle();
  const speed = toolsRand(0.5, 2);
  const size = toolsRand(0.2, 0.8);
  const life = toolsRand(0, 0.3);
  const distance = toolsRand(0, 5 * reach);
  const sinA = Math.sin(angle);
  const cosA = Math.cos(angle);
  explosions.addParticle(
    particleTypeIdByName.PLANE_DAMAGE,
    texture,
    sinA * speed + player.speed.x,
    -cosA * speed + player.speed.y,
    player.pos.x + sinA * distance,
    player.pos.y - cosA * distance,
    size,
    size,
    0,
    0,
    toolsRandCircle(),
    SPARK_TINT,
    'add',
    life,
  );
}

/** An explosion at a position: flashes, sparks, debris emitters, smoke, a glow and hot smoke, all scaled. */
export function explosion(position: Vector, scale: number, fragmentEmitters = 0) {
  const adjustedScale = scale > 1 ? 1 + (scale - 1) / 1.5 : scale;

  for (let i = 0; i < 2; i++) {
    const texture = toolsRandItem(flashTextures);
    const flashScale = 1.5 * scale;
    explosions.addParticle(
      particleTypeIdByName.EXPLOSION_FLASH,
      texture,
      0,
      0,
      position.x,
      position.y,
      flashScale,
      flashScale,
      0,
      0,
      toolsRandCircle(),
      FLASH_TINT,
      'add',
    );
  }

  const sparkCount = Math.round(toolsRand(20, 30) * scale);
  for (let i = 0; i < sparkCount; i++) {
    const texture = toolsRandItem(sparkTextures);
    const angle = toolsRandCircle();
    const speed = toolsRand(3, 10) * adjustedScale;
    const rotationSpeed = toolsRand(-0.2, 0.2);
    const sparkScale = toolsRand(0.4, 1.5) * adjustedScale;
    const life = toolsRand(0, 0.3);
    explosions.addParticle(
      particleTypeIdByName.EXPLOSION_SPARK,
      texture,
      Math.sin(angle) * speed,
      -Math.cos(angle) * speed,
      position.x,
      position.y,
      sparkScale,
      sparkScale,
      0,
      rotationSpeed,
      toolsRandCircle(),
      SPARK_TINT,
      'add',
      life,
    );
  }

  for (let i = 0; i < fragmentEmitters; i++) {
    const angle = toolsRandCircle();
    const speed = toolsRand(3, 7) * adjustedScale;
    const distance = toolsRand(15, 30) * scale;
    const sinA = Math.sin(angle);
    const cosA = Math.cos(angle);
    explosions.addEmitter(
      new Vector(sinA * speed, -cosA * speed),
      new Vector(position.x + sinA * distance, position.y - cosA * distance),
      toolsRand(0, 0.5),
      shadows,
    );
  }

  const hitshapes = isHitshapes();
  if (!hitshapes) {
    const smokeCount = Math.round(toolsRand(20, 30) * scale);
    for (let i = 0; i < smokeCount; i++) {
      const pair = toolsRandItem(smokePairs);
      const angle = toolsRandCircle();
      const distance = toolsRand(0, 10) * scale;
      const speed = toolsRand(0, 3) * scale;
      const rotationSpeed = toolsRand(-0.1, 0.1);
      const smokeScale = toolsRand(0.5, 0.8) * scale;
      const rotation = toolsRandCircle();
      const sinA = Math.sin(angle);
      const cosA = Math.cos(angle);
      explosions.addSmokePair(
        shadows,
        pair,
        particleTypeIdByName.EXPLOSION_SMOKE,
        sinA * speed,
        -cosA * speed,
        position.x + sinA * distance,
        position.y - cosA * distance,
        smokeScale,
        smokeScale,
        0,
        rotationSpeed,
        rotation,
      );
    }
  }

  // A missile hit, with no debris, loses its glow and hot smoke too in hitshapes
  if (!(hitshapes && fragmentEmitters === 0)) {
    const glowScale = 6 * scale;
    explosions.addParticle(
      particleTypeIdByName.EXPLOSION_FLASH_BIG,
      'glowsmall',
      0,
      0,
      position.x,
      position.y,
      glowScale,
      glowScale,
      0,
      0,
      0,
      undefined,
      'add',
    );
    const lightScale = 7 * scale;
    explosions.addParticle(
      particleTypeIdByName.EXPLOSION_LIGHT,
      'glowsmall',
      0,
      0,
      position.x,
      position.y,
      lightScale,
      lightScale,
      0,
      0,
      0,
      undefined,
      'add',
    );

    const hotSmokeCount = Math.round(toolsRand(5, 10) * scale);
    for (let i = 0; i < hotSmokeCount; i++) {
      const angle = toolsRandCircle();
      const speed = toolsRand(1, 3) * scale;
      const rotationSpeed = toolsRand(-0.1, 0.1);
      const hotScale = toolsRand(0.1, 0.3) * scale;
      const texture = toolsRandItem(hotsmokeTextures);
      explosions.addParticle(
        particleTypeIdByName.EXPLOSION_HOT_SMOKE,
        texture,
        Math.sin(angle) * speed,
        -Math.cos(angle) * speed,
        position.x,
        position.y,
        hotScale,
        hotScale,
        0,
        rotationSpeed,
        toolsRandCircle(),
        SPARK_TINT,
        'add',
      );
    }
  }
}

/** A puff of a missile's trail at its exhaust, sized by its visibility: 1 in flight, fading as it despawns. */
export function missileSmoke(mob: Mob, exhaust: number, visibility: number) {
  const trail = TRAIL_BY_MISSILE[mob.type];
  if (trail === undefined) {
    return;
  }
  const rotation = mob.spriteRot + Math.PI;
  const posX = mob.pos.x + Math.sin(rotation) * exhaust;
  const posY = mob.pos.y - Math.cos(rotation) * exhaust;
  const scale = 0.2 * visibility;
  const velAngle = rotation + toolsRand(-0.1, 0.1);
  const speedX = Math.sin(velAngle) * 5 * visibility;
  const speedY = -Math.cos(velAngle) * 5 * visibility;
  const pair = toolsRandItem(smokePairs);
  const rotationSpeed = toolsRand(-0.1, 0.1);
  smoke.addSmokePair(
    shadows,
    pair,
    trail,
    speedX,
    speedY,
    posX,
    posY,
    1.25 * scale,
    4 * scale,
    1,
    rotationSpeed,
    rotation,
    SMOKE_TINT,
    undefined,
    visibility,
  );
}

/** A puff of boost smoke behind the flame, or ahead of the plane when it brakes. */
export function planeBoost(player: Player, forwardThrust: boolean) {
  const thrustRotation = player.rot + player.state.thrustDir / 2 + Math.PI;
  const exhaustOffset = forwardThrust ? 40 : -20;
  const posX = player.pos.x + Math.sin(thrustRotation) * exhaustOffset;
  const posY = player.pos.y - Math.cos(thrustRotation) * exhaustOffset;
  const velAngle = thrustRotation + toolsRand(-0.1, 0.1) + (forwardThrust ? 0 : Math.PI);
  const speedX = Math.sin(velAngle) * 6;
  const speedY = -Math.cos(velAngle) * 6;
  const pair = toolsRandItem(smokePairs);
  const rotationSpeed = toolsRand(-0.1, 0.1);
  smoke.addSmokePair(
    shadows,
    pair,
    particleTypeIdByName.MISSILE,
    speedX,
    speedY,
    posX,
    posY,
    0.3,
    1.2,
    1,
    rotationSpeed,
    thrustRotation,
    SMOKE_TINT,
    undefined,
    1.2,
  );
}

/**
 * The ring of a repel: forty puffs outward and two expanding rings. They go in
 * the smoke container behind the planes, except in hitshapes, which hides that
 * whole container; SWAM moved them to the explosions container for that theme.
 */
export function spiritShockwave(position: Vector) {
  const container = isHitshapes() ? explosions : smoke;
  for (let i = 0; i < 40; i++) {
    const angle = (i / 40) * TWO_PI;
    const texture = toolsRandItem(smokeTextures);
    container.addParticle(
      particleTypeIdByName.SHOCKWAVE_SMOKE,
      texture,
      Math.sin(angle) * 8,
      -Math.cos(angle) * 8,
      position.x,
      position.y,
      2,
      2,
      0,
      0.4,
      toolsRandCircle(),
    );
  }
  container.addParticle(
    particleTypeIdByName.SHOCKWAVE_INNER,
    'shockwave',
    0,
    0,
    position.x,
    position.y,
    0,
    0,
    0,
    0,
    -0.35 + Math.PI,
  );
  container.addParticle(
    particleTypeIdByName.SHOCKWAVE_OUTER,
    'shockwave',
    0,
    0,
    position.x,
    position.y,
    0,
    0,
    0,
    0,
    -0.35,
  );
}

/** The live particle count over the three containers, for the debug overlay. */
export function count() {
  return smoke.count + explosions.count + shadows.count;
}
